import api from "@/services/api";
import { FilterValues } from "@/components/ui/FilterBar/types";
import {
  mergeInstallmentTotalsIntoCache,
  readInstallmentTotalsCacheForBillIds,
} from "@/lib/installmentTotalsCache";

/* ─── Tipos ──────────────────────────────────────────────────────────────── */

export interface OutcomeItem {
  billId:            string;
  issueDate:         string;
  dueDate:           string;
  paymentDate:       string;
  /**
   * Valor usado no Financeiro (card, modais, gráficos): quando o bulk traz `paymentsCategories`
   * e `payments`, segue o Power Query SEMOG — Σ(netAmount × financialCategoryRate / 100);
   * senão, o valor bruto da parcela no Sienge (`originalAmount` / gross / etc.).
   */
  originalAmount:    number;
  /** Presente quando `originalAmount` foi calculado por apropriação categoria × pagamento. */
  valorApropriado?:  number;
  balanceAmount:     number;
  isPaid:            boolean;
  creditorName:      string;
  documentType:      string;
  costCenterId:      string;
  costCenterName:    string;
  accountId:         string;
  accountName:       string;
  /** Código da categoria no Sienge (quando existir no bulk / payment-categories) */
  accountCode?:      string;
  /** Unidade construtiva (etapa do orçamento) — bulk-data / paymentsCategories */
  buildingUnitId?:   string;
  buildingUnitName?: string;
  /** Insumo / material vinculado à parcela (quando o bulk Sienge envia) */
  resourceId?:       string;
  resourceDescription?: string;
  description:       string;
  notes:             string;
  installmentNumber: number; /* número da parcela (1-based) */
  /** Total de parcelas do título (quando disponível no payload). */
  installmentsTotal?: number;
}

export interface IncomeItem {
  billId:         string;
  issueDate:      string;
  dueDate:        string;
  paymentDate:    string;
  originalAmount: number;
  balanceAmount:  number;
  isPaid:         boolean;
  /** ID do cliente no Sienge, quando o bulk envia. */
  clientId?:      string;
  /** CPF/CNPJ do cliente, quando o bulk envia. */
  clientDocument?: string;
  /** Identificador da parcela no título. */
  installmentId?: string;
  installmentNumber?: number;
  clientName:     string;
  /** Nome do tipo de documento no bulk (ex.: CONTRATO) — coluna «Documento» no modal Recebimentos */
  documentIdentificationName?: string;
  documentType:   string;
  description:    string;
  /** Unidade / imóvel (contas a receber — bulk-data), quando o Sienge envia */
  buildingUnitId?:   string;
  buildingUnitName?: string;
  /** Centro de custo / empreendimento (bulk income / Comercial) */
  costCenterId?:     string;
  costCenterName?:   string;
  /** Plano financeiro (`paymentsCategories` no bulk income) */
  accountId?:        string;
  accountName?:      string;
  /** Corretor / imobiliária (Comercial — bulk sales `brokers[].name`) */
  brokerName?:       string;
  /** Soma de `paymentConditions[].totalValue` da condição DE (desconto da venda). */
  discountAmount?:   number;
  /** Soma de `paymentConditions[].totalValue` exceto conditionTypeId PP/PF/DE (Comercial — pré-chave) */
  preChaveAmount?:   number;
  /** Data de distrato / cancelamento (bulk sales `cancellationDate`) */
  cancellationDate?: string;
  /** Motivo do cancelamento (bulk sales `cancellationReason`) */
  cancellationReason?: string;
  /** Soma dos objetos `receipts` no bulk income (Sienge) */
  receiptAdditionAmount?: number;
  receiptInsuranceAmount?: number;
  receiptDueAdmAmount?: number;
  receiptDiscountAmount?: number;
  /**
   * Bruto do lançamento em `receipts[]` (Sienge): `grossAmount`.
   * Quando há vários recebimentos no mesmo título, cada linha espelha um receipt.
   */
  receiptGrossAmount?: number;
  /**
   * Líquido oficial do recebimento (`receipt.netAmount`) — mesmo conceito do Power Query
   * após expandir `receipts`; substitui o cálculo manual quando informado.
   */
  receiptNetAmount?: number;
}

/**
 * Valor recebido para KPI / subtotal: prioriza `netAmount` do receipt (bulk Sienge),
 * senão composição Valor + acréscimos − desconto na parcela.
 */
export function incomeReceiptLiquido(item: IncomeItem): number {
  if (item.receiptNetAmount != null && Number.isFinite(item.receiptNetAmount)) {
    return item.receiptNetAmount;
  }
  const valor = item.originalAmount;
  const acrescimo = item.receiptAdditionAmount ?? 0;
  const seguro = item.receiptInsuranceAmount ?? 0;
  const taxaAdm = item.receiptDueAdmAmount ?? 0;
  const desconto = item.receiptDiscountAmount ?? 0;
  return valor + acrescimo + seguro + taxaAdm - desconto;
}

/** Valor da parcela para KPI / tabelas: `valorApropriado` quando calculado no bulk; senão `originalAmount`. */
export function outcomeParcelValorExibido(item: OutcomeItem): number {
  const v = item.valorApropriado;
  if (v != null && Number.isFinite(v) && v > 0) return v;
  return item.originalAmount;
}

/** Movimento de extrato bancário (API Sienge accounts-statements), contas SEMOG. */
export interface BankStatementMovement {
  id: string;
  movementDate: string;
  /** Valor de entrada (crédito) */
  credit: number;
  /** Valor de saída (débito), sempre ≥ 0 */
  debit: number;
  description: string;
  accountNumber: string;
  accountLabel: string;
}

/** Linha bulk `bank-movement` (origin BC, operação Pagamento/Recebimento) — mesma base do KPI. */
export interface BankMovementFinanceiroLine {
  id: string;
  movementDate: string;
  amount: number;
  operationName: string;
  historico: string;
}

export interface FinanceiroData {
  outcome: OutcomeItem[];
  income: IncomeItem[];
  statements: BankStatementMovement[];
  /**
   * Soma bulk `bank-movement` (selectionType=M, histórico contém «Recebime»), companyId 1.
   * Quando definido, o card «Recebimentos» usa este total (Power Query SEMOG).
   */
  recebimentosBankMovementTotal?: number;
  /** Bulk bank-movement (Power Query): operação Pagamento */
  pagamentosBankMovementTotal?: number;
  /** Bulk bank-movement (Power Query): operação Recebimento */
  recebimentosBankMovementOperacaoTotal?: number;
  /** Movimentos de operação Pagamento (lista detalhada para modal). */
  bankMovementPagamentoLines?: BankMovementFinanceiroLine[];
  /** Movimentos de operação Recebimento (lista detalhada para modal). */
  bankMovementRecebimentoLines?: BankMovementFinanceiroLine[];
}

/**
 * Movimentos bulk bank-movement (Recebimento e Pagamento) não vêm com centro de custo no Sienge.
 * Só devem compor os cards/modais quando não há filtro de CC ou quando todos os CCs estão selecionados.
 * O mesmo critério vale para o bulk de Pagamentos (sem CC no Sienge).
 */
export function includeRecebimentosBankMovementForCostCenterFilter(
  selectedCostCenterIds: string[],
  allCostCenterIds: string[] | undefined
): boolean {
  if (!allCostCenterIds || allCostCenterIds.length === 0) return true;
  const sel = (selectedCostCenterIds ?? []).map((s) => String(s).trim()).filter(Boolean);
  if (sel.length === 0) return true;

  const allSet = new Set(allCostCenterIds.map((s) => String(s).trim()).filter(Boolean));
  if (allSet.size === 0) return true;
  if (sel.length !== allSet.size) return false;

  for (const id of sel) {
    if (!allSet.has(id)) return false;
  }
  return true;
}

/** Mesmo critério de {@link includeRecebimentosBankMovementForCostCenterFilter} — bulk «Pagamento» sem CC no Sienge. */
export function includePagamentosBankMovementForCostCenterFilter(
  selectedCostCenterIds: string[],
  allCostCenterIds: string[] | undefined
): boolean {
  return includeRecebimentosBankMovementForCostCenterFilter(selectedCostCenterIds, allCostCenterIds);
}

/* ─── Helpers internos ───────────────────────────────────────────────────── */

function getStr(o: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = o[k];
    if (v != null) {
      const s = String(v).trim();
      if (s) return s;
    }
  }
  return "";
}

function extractIncomeClientId(o: Record<string, unknown>): string {
  const direct = getStr(
    o,
    "customerId", "clientId", "customer_id", "client_id", "idCustomer", "id_customer",
  );
  if (direct) return direct;
  for (const key of ["customer", "client", "buyer", "businessPartner", "person"]) {
    const v = o[key];
    if (v != null && typeof v === "object" && !Array.isArray(v)) {
      const id = getStr(v as Record<string, unknown>, "id", "customerId", "clientId", "code");
      if (id) return id;
    }
  }
  const inst = o.installment ?? o.installment_ ?? o.parcel;
  if (inst != null && typeof inst === "object" && !Array.isArray(inst)) {
    return extractIncomeClientId(inst as Record<string, unknown>);
  }
  return "";
}

function extractIncomeClientDocument(o: Record<string, unknown>): string {
  const direct = getStr(
    o,
    "clientDocument", "customerDocument", "documentNumber", "cpf", "cnpj", "cpfCnpj", "registerNumber",
  );
  if (direct) return direct;
  for (const key of ["customer", "client", "buyer"]) {
    const v = o[key];
    if (v != null && typeof v === "object" && !Array.isArray(v)) {
      const doc = getStr(
        v as Record<string, unknown>,
        "documentNumber", "cpf", "cnpj", "cpfCnpj", "registerNumber", "taxId",
      );
      if (doc) return doc;
    }
  }
  return "";
}

function extractIncomeInstallment(o: Record<string, unknown>): { id: string; number: number } {
  const inst = (o.installment ?? o.installment_ ?? o.parcel) as Record<string, unknown> | undefined;
  const id = getStr(
    o,
    "installmentId", "installment_id", "installmentNumber", "installment_number",
    "parcelNumber", "parcel_number",
  ) || (inst ? getStr(inst, "id", "installmentId", "number", "installmentNumber") : "");
  const number = getNum(
    o,
    "installmentNumber", "installment_number", "installmentId", "installment_id",
    "parcelNumber", "numberInstallment",
  ) || (inst ? getNum(inst, "number", "installmentNumber", "installmentId") : 0);
  return { id, number };
}

function getNum(o: Record<string, unknown>, ...keys: string[]): number {
  for (const k of keys) {
    const v = o[k];
    if (v != null) {
      const n = typeof v === "number" ? v : parseFloat(String(v).replace(",", "."));
      if (!Number.isNaN(n)) return n;
    }
  }
  return 0;
}

/** Para campos alternativos no Sienge: prefere o primeiro valor com módulo > 0; senão o último numérico encontrado. */
function pickNumFromRow(o: Record<string, unknown>, keys: readonly string[]): number {
  let last: number | undefined;
  for (const k of keys) {
    const v = o[k];
    if (v == null || v === "") continue;
    const n = typeof v === "number" ? v : parseFloat(String(v).replace(",", "."));
    if (Number.isNaN(n)) continue;
    if (Math.abs(n) > 1e-12) return n;
    last = n;
  }
  return last ?? 0;
}

function getFirstPaymentDate(payments: unknown): string {
  if (!Array.isArray(payments) || payments.length === 0) return "";
  let latest = "";
  for (const p of payments) {
    if (!p || typeof p !== "object") continue;
    const o = p as Record<string, unknown>;
    const d = getStr(
      o,
      "paymentDate",
      "payment_date",
      "date",
      "movementDate",
      "movement_date",
      "paidDate",
      "paid_date",
      "bankMovementDate",
      "bank_movement_date",
      "realizationDate",
      "realization_date",
      "accountingDate",
      "accounting_date"
    ).substring(0, 10);
    if (!d) continue;
    if (!latest || d > latest) latest = d;
  }
  return latest;
}

/**
 * Data de pagamento no bulk contas a pagar: pode vir em `payments`,
 * campos na raiz ou dentro de `installment` / `parcel`.
 */
function getOutcomePaymentDateFromRaw(o: Record<string, unknown>): string {
  let d = getFirstPaymentDate(o.payments);
  if (d) return d.substring(0, 10);
  d = getFirstPaymentDate(o.bankMovements);
  if (d) return d.substring(0, 10);
  d = getStr(
    o,
    "paymentDate",
    "payment_date",
    "paidDate",
    "paid_date",
    "settlementDate",
    "settlement_date",
    "movementDate",
    "movement_date",
    "accountingDate"
  ).substring(0, 10);
  if (d) return d;

  const inst = o.installment ?? o.installment_ ?? o.parcel;
  if (inst && typeof inst === "object" && !Array.isArray(inst)) {
    const io = inst as Record<string, unknown>;
    d = getFirstPaymentDate(io.payments) || getFirstPaymentDate(io.bankMovements);
    if (d) return d.substring(0, 10);
    d = getStr(
      io,
      "paymentDate",
      "payment_date",
      "paidDate",
      "paid_date",
      "settlementDate",
      "movementDate"
    ).substring(0, 10);
    if (d) return d;
  }
  return "";
}

/**
 * Data de recebimento no bulk contas a receber: o Sienge pode usar `payments`,
 * `receipts`, campos na raiz ou dentro de `installment`.
 */
function getIncomePaymentDateFromRaw(o: Record<string, unknown>): string {
  let d = getFirstPaymentDate(o.payments);
  if (d) return d.substring(0, 10);
  d = getFirstPaymentDate(o.receipts);
  if (d) return d.substring(0, 10);
  d = getFirstPaymentDate(o.receipt);
  if (d) return d.substring(0, 10);
  d = getFirstPaymentDate(o.bankMovements);
  if (d) return d.substring(0, 10);
  d = getStr(
    o,
    "paymentDate",
    "payment_date",
    "receiptDate",
    "receipt_date",
    "paidDate",
    "paid_date",
    "settlementDate",
    "lastReceiptDate",
    "movementDate",
    "accountingDate"
  ).substring(0, 10);
  if (d) return d;

  const inst = o.installment ?? o.installment_ ?? o.parcel;
  if (inst && typeof inst === "object" && !Array.isArray(inst)) {
    const io = inst as Record<string, unknown>;
    d = getFirstPaymentDate(io.payments) || getFirstPaymentDate(io.receipts);
    if (d) return d.substring(0, 10);
    d = getStr(
      io,
      "paymentDate",
      "payment_date",
      "receiptDate",
      "paidDate"
    ).substring(0, 10);
    if (d) return d;
  }

  return "";
}

/** Uma linha de recebimento / parcela no bulk income — vários nomes usados pelo Sienge. */
function incomeReceiptAdditionFromRow(r: Record<string, unknown>): number {
  return pickNumFromRow(r, [
    "receiptAdditionAmount",
    "receipt_addition_amount",
    "totalAdditionAmount",
    "total_addition_amount",
    "additionAmount",
    "addition_amount",
    "increaseAmount",
    "increase_amount",
    "interestAmount",
    "interest_amount",
    "fineAmount",
    "fine_amount",
    "mulctAmount",
    "mulct_amount",
    "monetaryCorrectionAmount",
    "monetary_correction_amount",
    "acrescimo",
    "valorAcrescimo",
    "valor_acrescimo",
    "valorTotalAcrescimo",
    "valor_total_acrescimo",
  ] as const);
}

function incomeReceiptInsuranceFromRow(r: Record<string, unknown>): number {
  return pickNumFromRow(r, [
    "receiptInsuranceAmount",
    "receipt_insurance_amount",
    "insurancePremiumAmount",
    "insurance_premium_amount",
    "insuranceAmount",
    "insurance_amount",
    "premiumAmount",
    "premium_amount",
    "seguro",
    "valorSeguro",
    "valor_seguro",
  ] as const);
}

function incomeReceiptDueAdmFromRow(r: Record<string, unknown>): number {
  return pickNumFromRow(r, [
    "receiptDueAdmAmount",
    "receipt_due_adm_amount",
    "dueAdmAmount",
    "due_adm_amount",
    "administrationFeeAmount",
    "administration_fee_amount",
    "administrationTaxAmount",
    "administration_tax_amount",
    "admFeeAmount",
    "adm_fee_amount",
    "managementFeeAmount",
    "management_fee_amount",
    "taxaAdministracao",
    "taxa_administracao",
    "taxaAdm",
    "taxa_adm",
  ] as const);
}

function incomeReceiptDiscountFromRow(r: Record<string, unknown>): number {
  return pickNumFromRow(r, [
    "receiptDiscountAmount",
    "receipt_discount_amount",
    "totalDiscountAmount",
    "total_discount_amount",
    "discountAmount",
    "discount_amount",
    "rebateAmount",
    "rebate_amount",
    "desconto",
    "valorDesconto",
    "valor_desconto",
  ] as const);
}

/**
 * Soma acréscimo, seguro, taxa de administração e desconto no bulk income:
 * `receipts` / `payments` / objeto único `receipt`, e fallback na raiz ou em `installment`
 * quando não há linhas de recebimento (títulos em aberto costumam trazer totais na parcela).
 */
function sumIncomeReceiptAmountsFromRaw(o: Record<string, unknown>): {
  addition: number;
  insurance: number;
  dueAdm: number;
  discount: number;
} {
  const acc = { addition: 0, insurance: 0, dueAdm: 0, discount: 0 };
  const consume = (receipts: unknown) => {
    if (receipts == null) return;
    const list = Array.isArray(receipts) ? receipts : [receipts];
    for (const p of list) {
      if (!p || typeof p !== "object") continue;
      const r = p as Record<string, unknown>;
      acc.addition += incomeReceiptAdditionFromRow(r);
      acc.insurance += incomeReceiptInsuranceFromRow(r);
      acc.dueAdm += incomeReceiptDueAdmFromRow(r);
      acc.discount += incomeReceiptDiscountFromRow(r);
    }
  };
  consume(o.receipts);
  consume(o.receipt);
  const inst = o.installment ?? o.installment_ ?? o.parcel;
  if (inst && typeof inst === "object" && !Array.isArray(inst)) {
    const io = inst as Record<string, unknown>;
    consume(io.receipts);
    consume(io.receipt);
  }

  const allZero =
    Math.abs(acc.addition) < 0.0005 &&
    Math.abs(acc.insurance) < 0.0005 &&
    Math.abs(acc.dueAdm) < 0.0005 &&
    Math.abs(acc.discount) < 0.0005;

  /* Alguns ambientes mandam o mesmo breakdown em `payments`. */
  if (allZero) {
    consume(o.payments);
    if (inst && typeof inst === "object" && !Array.isArray(inst)) {
      consume((inst as Record<string, unknown>).payments);
    }
  }

  if (Math.abs(acc.addition) < 0.0005) {
    acc.addition = Math.max(incomeReceiptAdditionFromRow(o), inst && typeof inst === "object" && !Array.isArray(inst)
      ? incomeReceiptAdditionFromRow(inst as Record<string, unknown>)
      : 0);
  }
  if (Math.abs(acc.insurance) < 0.0005) {
    acc.insurance = Math.max(
      incomeReceiptInsuranceFromRow(o),
      inst && typeof inst === "object" && !Array.isArray(inst)
        ? incomeReceiptInsuranceFromRow(inst as Record<string, unknown>)
        : 0
    );
  }
  if (Math.abs(acc.dueAdm) < 0.0005) {
    acc.dueAdm = Math.max(
      incomeReceiptDueAdmFromRow(o),
      inst && typeof inst === "object" && !Array.isArray(inst)
        ? incomeReceiptDueAdmFromRow(inst as Record<string, unknown>)
        : 0
    );
  }
  if (Math.abs(acc.discount) < 0.0005) {
    acc.discount = Math.max(
      incomeReceiptDiscountFromRow(o),
      inst && typeof inst === "object" && !Array.isArray(inst)
        ? incomeReceiptDiscountFromRow(inst as Record<string, unknown>)
        : 0
    );
  }

  return acc;
}

/** Código do plano financeiro em uma linha de `paymentsCategories` (vários nomes no Sienge). */
function financialCategoryCodeFromCat(c: Record<string, unknown>): string {
  return getStr(
    c,
    "financialCategoryCode",
    "financial_category_code",
    "categoryCode",
    "category_code",
    "paymentCategoryCode",
    "payment_category_code",
    "accountPlanCode",
    "account_plan_code",
    "accountingCode",
    "accounting_code",
    "chartOfAccountsCode",
    "chart_of_accounts_code",
    "planAccountCode",
    "hierarchyCode",
    "planCode",
    "codigo",
    "code"
  );
}

/** Empilha arrays de categorias sem duplicar a mesma referência de objeto. */
function pushCategoryObjects(
  out: Record<string, unknown>[],
  seen: Set<unknown>,
  arr: unknown,
): void {
  if (!Array.isArray(arr)) return;
  for (const x of arr) {
    if (!x || typeof x !== "object") continue;
    if (seen.has(x)) continue;
    seen.add(x);
    out.push(x as Record<string, unknown>);
  }
}

/** `paymentsCategories[]` na raiz ou dentro de `installment` (bulk outcome Sienge). */
function collectOutcomePaymentsCategories(o: Record<string, unknown>): Record<string, unknown>[] {
  const out: Record<string, unknown>[] = [];
  const seen = new Set<unknown>();
  pushCategoryObjects(out, seen, o.paymentsCategories);
  const inst = o.installment ?? o.installment_ ?? o.parcel ?? o.paymentInstallment;
  if (inst && typeof inst === "object" && !Array.isArray(inst)) {
    pushCategoryObjects(out, seen, (inst as Record<string, unknown>).paymentsCategories);
  }
  return out;
}

/**
 * Categorias financeiras no bulk income: além de `paymentsCategories` (raiz/installment),
 * o Sienge pode enviar outros arrays `*Categories` — necessários para Plano Financeiro.
 */
function collectIncomePaymentsCategories(o: Record<string, unknown>): Record<string, unknown>[] {
  const out = collectOutcomePaymentsCategories(o);
  const seen = new Set<unknown>(out);
  for (const key of Object.keys(o)) {
    if (!/categories$/i.test(key)) continue;
    pushCategoryObjects(out, seen, o[key]);
  }
  const inst = o.installment ?? o.installment_ ?? o.parcel ?? o.paymentInstallment;
  if (inst && typeof inst === "object" && !Array.isArray(inst)) {
    const io = inst as Record<string, unknown>;
    for (const key of Object.keys(io)) {
      if (!/categories$/i.test(key)) continue;
      pushCategoryObjects(out, seen, io[key]);
    }
  }
  return out;
}

const INCOME_ACCOUNT_ID_KEYS = [
  "financialCategoryId",
  "paymentCategoryId",
  "categoryId",
  "financial_category_id",
  "payment_category_id",
  "accountPlanId",
  "accountId",
  "account_id",
] as const;

const INCOME_ACCOUNT_NAME_KEYS = [
  "financialCategoryName",
  "paymentCategoryName",
  "categoryName",
  "financial_category_name",
  "payment_category_name",
  "accountPlanName",
  "accountName",
  "account_name",
] as const;

function accountFieldsFromCategoryRow(c: Record<string, unknown>): { id: string; name: string } {
  return {
    id: getStr(c, ...INCOME_ACCOUNT_ID_KEYS),
    name: getStr(c, ...INCOME_ACCOUNT_NAME_KEYS),
  };
}

/** `payments[]` na raiz ou dentro de `installment`; se vazio, tenta dentro de cada `paymentsCategories[]`. */
function collectOutcomePayments(o: Record<string, unknown>): Record<string, unknown>[] {
  const out: Record<string, unknown>[] = [];
  const seen = new Set<unknown>();
  const push = (arr: unknown) => {
    if (!Array.isArray(arr)) return;
    for (const x of arr) {
      if (x && typeof x === "object") {
        if (seen.has(x)) continue;
        seen.add(x);
        out.push(x as Record<string, unknown>);
      }
    }
  };
  push(o.payments);
  const inst = o.installment ?? o.installment_ ?? o.parcel ?? o.paymentInstallment;
  if (inst && typeof inst === "object" && !Array.isArray(inst)) {
    push((inst as Record<string, unknown>).payments);
  }
  if (out.length === 0) {
    for (const cat of collectOutcomePaymentsCategories(o)) {
      push((cat as Record<string, unknown>).payments);
    }
  }
  return out;
}

/** Campos de taxa em `paymentsCategories[]` / `paymentCategories[]` (bulk Sienge). */
const OUTCOME_CAT_RATE_KEYS = [
  "financialCategoryRate",
  "financial_category_rate",
  "categoryRate",
  "category_rate",
  "paymentCategoryRate",
  "payment_category_rate",
  "financialCategoryPercentage",
  "financial_category_percentage",
  "financialCategoryPercent",
  "allocationPercentage",
  "allocation_percentage",
  "percentage",
  "percent",
  "percentual",
  "rate",
] as const;

/** Campos de valor líquido em `payments[]` (bulk Sienge). */
const OUTCOME_PAY_NET_KEYS = [
  "netAmount",
  "net_amount",
  "correctedNetAmount",
  "corrected_net_amount",
  "paymentNetAmount",
  "payment_net_amount",
  "grossAmount",
  "gross_amount",
  "paidAmount",
  "paid_amount",
  "amount",
  "value",
] as const;

function effectiveOutcomeCategoryRates(cats: Record<string, unknown>[]): number[] {
  const ratesRaw = cats.map((c) => pickNumFromRow(c as Record<string, unknown>, OUTCOME_CAT_RATE_KEYS));
  const sumRates = ratesRaw.reduce((a, b) => a + b, 0);
  if (sumRates < 1e-9 && cats.length > 0) {
    return cats.map(() => 100 / cats.length);
  }
  return ratesRaw;
}

/**
 * Power Query SEMOG: valorApropriado = netAmount × financialCategoryRate / 100
 * para cada par (categoria de pagamento × lançamento em `payments`).
 */
function sumOutcomeValorApropriado(o: Record<string, unknown>): number | null {
  const cats = collectOutcomePaymentsCategories(o);
  const pays = collectOutcomePayments(o);
  if (cats.length === 0 || pays.length === 0) return null;

  const effRates = effectiveOutcomeCategoryRates(cats);

  let sum = 0;
  let pairs = 0;
  for (let ci = 0; ci < cats.length; ci++) {
    const rate = effRates[ci] ?? 0;
    for (const pay of pays) {
      const net = pickNumFromRow(pay as Record<string, unknown>, OUTCOME_PAY_NET_KEYS);
      sum += net * (rate / 100);
      pairs++;
    }
  }
  if (pairs === 0) return null;
  if (!Number.isFinite(sum)) return null;
  if (Math.abs(sum) < 1e-9) return null;
  return sum;
}

/**
 * O código costuma existir em alguma alocação da lista, não só na primeira.
 * Se `preferredAccountId` existir, prioriza a linha cuja categoria coincide.
 */
function resolveAccountCodeFromPaymentsCategories(
  payCatArr: Record<string, unknown>[],
  preferredAccountId: string
): string {
  const idMatch = (c: Record<string, unknown>): boolean => {
    if (!preferredAccountId) return false;
    const cid = getStr(
      c,
      "financialCategoryId",
      "financial_category_id",
      "paymentCategoryId",
      "payment_category_id",
      "categoryId",
      "category_id"
    );
    if (!cid) return false;
    if (cid === preferredAccountId) return true;
    if (/^\d+$/.test(cid) && /^\d+$/.test(preferredAccountId)) {
      return Number(cid) === Number(preferredAccountId);
    }
    return false;
  };

  if (preferredAccountId) {
    for (const cat of payCatArr) {
      if (!cat || typeof cat !== "object") continue;
      const c = cat as Record<string, unknown>;
      if (!idMatch(c)) continue;
      const code = financialCategoryCodeFromCat(c);
      if (code) return code;
    }
  }
  for (const cat of payCatArr) {
    if (!cat || typeof cat !== "object") continue;
    const code = financialCategoryCodeFromCat(cat as Record<string, unknown>);
    if (code) return code;
  }
  return "";
}

/**
 * Uma linha bulk por alocação em `paymentsCategories`: valor = Σ_payments (net × rate/100)
 * para aquela categoria — espelha o expand do Power Query (vários CC no mesmo título).
 * Quando não há categorias × pagamentos, usa {@link normalizeOutcomeItemSingle}.
 */
function normalizeOutcomeRowToItems(raw: unknown): OutcomeItem[] {
  if (!raw || typeof raw !== "object") return [];
  const o = raw as Record<string, unknown>;

  const payCatArr = collectOutcomePaymentsCategories(o);
  const pays = collectOutcomePayments(o);

  if (payCatArr.length > 0 && pays.length > 0) {
    const effRates = effectiveOutcomeCategoryRates(payCatArr);
    const parcelAmountFallback = getNum(o,
      "originalAmount", "original_amount",
      "correctedOriginalAmount", "grossAmount", "amount", "value"
    );
    const balanceAmount =
      getNum(o,
        "balanceAmount", "balance_amount",
        "correctedBalanceAmount", "corrected_balance_amount",
        "currentBalance", "balance"
      ) || parcelAmountFallback;
    const paymentDateStr = getOutcomePaymentDateFromRaw(o);
    const isPaid = paymentDateStr !== "";
    const payCat0 = payCatArr[0] ?? null;
    const ccObj = (o.costCenter ?? o.cost_center) as Record<string, unknown> | undefined;
    const buNested = (o.buildingUnit ?? o.building_unit ?? o.constructionUnit) as
      | Record<string, unknown>
      | undefined;

    let rootBuildingUnitId =
      getStr(o, "buildingUnitId", "building_unit_id", "constructionUnitId", "unitConstructionId") ||
      (buNested ? getStr(buNested, "id", "buildingUnitId", "building_unit_id", "code") : "");
    let rootBuildingUnitName =
      getStr(o, "buildingUnitName", "building_unit_name", "constructionUnitName", "unitConstructionName") ||
      (buNested ? getStr(buNested, "name", "description", "buildingUnitName", "title") : "");
    if (payCat0) {
      rootBuildingUnitId =
        rootBuildingUnitId ||
        getStr(
          payCat0 as Record<string, unknown>,
          "buildingUnitId",
          "building_unit_id",
          "constructionUnitId",
          "unitConstructionId"
        );
      rootBuildingUnitName =
        rootBuildingUnitName ||
        getStr(
          payCat0 as Record<string, unknown>,
          "buildingUnitName",
          "building_unit_name",
          "constructionUnitName"
        );
    }
    for (const cat of payCatArr) {
      if (!cat || typeof cat !== "object") continue;
      const cx = cat as Record<string, unknown>;
      const cid = getStr(cx, "buildingUnitId", "building_unit_id", "constructionUnitId");
      const cname = getStr(cx, "buildingUnitName", "building_unit_name", "constructionUnitName");
      if (cid) rootBuildingUnitId = rootBuildingUnitId || cid;
      if (cname) rootBuildingUnitName = rootBuildingUnitName || cname;
      if (rootBuildingUnitId && rootBuildingUnitName) break;
    }

    const accountCodeFromRoot = getStr(
      o,
      "financialCategoryCode",
      "financial_category_code",
      "paymentCategoryCode",
      "categoryCode",
      "accountPlanCode",
      "accountCode",
      "account_code"
    );

    const resourceId = getStr(
      o,
      "productId",
      "product_id",
      "resourceId",
      "resource_id",
      "insumoId",
      "insumo_id",
      "materialId",
      "material_id"
    );
    const resourceDescription = getStr(
      o,
      "productDescription",
      "product_description",
      "resourceDescription",
      "resource_description",
      "materialDescription",
      "material_description",
      "insumoDescription",
      "insumo_description",
      "serviceDetail",
      "service_detail",
      "itemDescription",
      "item_description",
      "productName",
      "product_name"
    );

    const installmentNumber =
      getNum(o,
        "installmentId", "installment_id",
        "installmentNumber", "installment_number",
        "parcelNumber", "parcel_number",
        "numberInstallment", "number_installment",
        "parcela", "parcel"
      ) || 1;
    const installmentsTotal = (() => {
      const total = getNum(
        o,
        "installmentsNumber",
        "installments_number",
        "totalInstallments",
        "total_installments",
        "numberInstallments",
        "number_installments"
      );
      return total > 0 ? total : undefined;
    })();

    const expanded: OutcomeItem[] = [];
    for (let i = 0; i < payCatArr.length; i++) {
      const cat = payCatArr[i];
      if (!cat || typeof cat !== "object") continue;
      const c = cat as Record<string, unknown>;
      const rate = effRates[i] ?? 0;
      let slice = 0;
      for (const pay of pays) {
        slice += pickNumFromRow(pay as Record<string, unknown>, OUTCOME_PAY_NET_KEYS) * (rate / 100);
      }
      if (!Number.isFinite(slice) || slice <= 1e-9) continue;

      const costCenterId =
        getStr(c, "costCenterId", "cost_center_id") ||
        getStr(o, "costCenterId", "cost_center_id", "costCenterCode", "cost_center_code") ||
        (payCat0 ? getStr(payCat0 as Record<string, unknown>, "costCenterId", "cost_center_id") : "") ||
        (ccObj ? getStr(ccObj, "id", "costCenterId", "cost_center_id", "code") : "");
      const costCenterName =
        getStr(c, "costCenterName", "cost_center_name") ||
        getStr(o, "costCenterName", "cost_center_name", "costCenterDescription") ||
        (payCat0 ? getStr(payCat0 as Record<string, unknown>, "costCenterName", "cost_center_name") : "") ||
        (ccObj ? getStr(ccObj, "name", "description", "costCenterName") : "");

      const accountId =
        getStr(c, "financialCategoryId", "paymentCategoryId", "categoryId") ||
        (payCat0
          ? getStr(payCat0 as Record<string, unknown>, "financialCategoryId", "paymentCategoryId", "categoryId")
          : "") ||
        getStr(o, "financialCategoryId", "financial_category_id", "paymentCategoryId",
          "payment_category_id", "accountPlanId", "accountId", "account_id");
      const accountName =
        getStr(c, "financialCategoryName", "paymentCategoryName", "categoryName") ||
        (payCat0
          ? getStr(
            payCat0 as Record<string, unknown>,
            "financialCategoryName",
            "paymentCategoryName",
            "categoryName"
          )
          : "") ||
        getStr(o, "financialCategoryName", "financial_category_name", "paymentCategoryName",
          "payment_category_name", "accountPlanName", "accountName", "account_name");

      const accountCodeFromCat = financialCategoryCodeFromCat(c);
      const accountCode =
        accountCodeFromCat ||
        resolveAccountCodeFromPaymentsCategories(payCatArr, accountId) ||
        accountCodeFromRoot;

      const buildingUnitId =
        getStr(c, "buildingUnitId", "building_unit_id", "constructionUnitId", "unitConstructionId") ||
        rootBuildingUnitId;
      const buildingUnitName =
        getStr(c, "buildingUnitName", "building_unit_name", "constructionUnitName", "unitConstructionName") ||
        rootBuildingUnitName;

      expanded.push({
        billId: getStr(o, "billId", "bill_id", "id"),
        issueDate: getStr(o, "issueDate", "issue_date", "emissionDate", "documentDate", "createdAt").substring(0, 10),
        dueDate: getStr(o, "dueDate", "due_date", "expirationDate", "maturityDate").substring(0, 10),
        paymentDate: paymentDateStr,
        originalAmount: slice,
        valorApropriado: slice,
        balanceAmount,
        isPaid,
        creditorName: getStr(o,
          "creditorName", "creditor_name",
          "supplierName", "supplier_name",
          "businessPartnerName", "personName", "companyName", "name"
        ),
        documentType: getStr(o,
          "documentIdentificationName", "documentIdentificationId",
          "documentType", "document_type", "docType"
        ),
        costCenterId,
        costCenterName,
        accountId,
        accountName,
        accountCode: accountCode || undefined,
        buildingUnitId: buildingUnitId || undefined,
        buildingUnitName: buildingUnitName || undefined,
        resourceId: resourceId || undefined,
        resourceDescription: resourceDescription || undefined,
        description: getStr(o, "description", "observation", "detailDescription"),
        notes: getStr(o, "notes", "note", "observation", "comments", "comment", "memo"),
        installmentNumber,
        installmentsTotal,
      });
    }
    if (expanded.length > 0) return expanded;
  }

  const one = normalizeOutcomeItemSingle(raw);
  return one ? [one] : [];
}

/** Parcela única quando não há explosão por `paymentsCategories` × `payments`. */
function normalizeOutcomeItemSingle(raw: unknown): OutcomeItem | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;

  const parcelAmountFallback = getNum(o,
    "originalAmount", "original_amount",
    "correctedOriginalAmount", "grossAmount", "amount", "value"
  );
  const valorApropriadoSum = sumOutcomeValorApropriado(o);
  const usedValorApropriado = valorApropriadoSum != null && valorApropriadoSum > 0;
  const originalAmount = usedValorApropriado ? valorApropriadoSum! : parcelAmountFallback;
  if (originalAmount <= 0) return null;

  const balanceAmount = getNum(o,
    "balanceAmount", "balance_amount",
    "correctedBalanceAmount", "corrected_balance_amount",
    "currentBalance", "balance"
  ) || parcelAmountFallback;

  const paymentDateStr = getOutcomePaymentDateFromRaw(o);

  /*
   * Regra de negócio: se existe data de pagamento → título está Pago.
   * A lógica anterior (balanceAmount <= 0) falha porque o Sienge pode retornar
   * saldo parcial ou o campo com valor diferente de zero mesmo após quitação.
   */
  const isPaid = paymentDateStr !== "";

  /*
   * ── paymentsCategories: array no bulk-data Sienge ──
   * Estrutura real confirmada via diagnóstico:
   * paymentsCategories[0] = {
   *   costCenterId, costCenterName,
   *   financialCategoryId, financialCategoryName,
   *   ...
   * }
   */
  const payCatArr = collectOutcomePaymentsCategories(o);
  const payCat0 = payCatArr.length > 0 ? payCatArr[0] : null;

  /* ── Centro de Custo ── */
  const ccObj = (o.costCenter ?? o.cost_center) as Record<string, unknown> | undefined;
  const costCenterId =
    getStr(o, "costCenterId", "cost_center_id", "costCenterCode", "cost_center_code") ||
    (payCat0 ? getStr(payCat0, "costCenterId", "cost_center_id") : "") ||
    (ccObj   ? getStr(ccObj,   "id", "costCenterId", "cost_center_id", "code") : "");
  const costCenterName =
    getStr(o, "costCenterName", "cost_center_name", "costCenterDescription") ||
    (payCat0 ? getStr(payCat0, "costCenterName", "cost_center_name") : "") ||
    (ccObj   ? getStr(ccObj,   "name", "description", "costCenterName") : "");

  /*
   * ── Plano Financeiro (financialCategory) ──
   * O nome já vem no bulk-data dentro de paymentsCategories[0].financialCategoryName.
   * Não é necessário chamar a API REST de payment-categories separadamente.
   */
  const accountId =
    (payCat0 ? getStr(payCat0, "financialCategoryId", "paymentCategoryId", "categoryId") : "") ||
    getStr(o, "financialCategoryId", "financial_category_id", "paymentCategoryId",
               "payment_category_id", "accountPlanId", "accountId", "account_id");

  const accountName =
    (payCat0 ? getStr(payCat0, "financialCategoryName", "paymentCategoryName", "categoryName") : "") ||
    getStr(o, "financialCategoryName", "financial_category_name", "paymentCategoryName",
               "payment_category_name", "accountPlanName", "accountName", "account_name");

  const accountCodeFromRoot = getStr(
    o,
    "financialCategoryCode",
    "financial_category_code",
    "paymentCategoryCode",
    "categoryCode",
    "accountPlanCode",
    "accountCode",
    "account_code"
  );
  const accountCodeFromCats = resolveAccountCodeFromPaymentsCategories(payCatArr, accountId);
  const accountCode = accountCodeFromCats || accountCodeFromRoot;

  /* ── Unidade construtiva (planilha / etapa — mesmo conceito do Orçamento) ── */
  const buNested = (o.buildingUnit ?? o.building_unit ?? o.constructionUnit) as
    | Record<string, unknown>
    | undefined;

  let buildingUnitId =
    getStr(o, "buildingUnitId", "building_unit_id", "constructionUnitId", "unitConstructionId") ||
    (buNested ? getStr(buNested, "id", "buildingUnitId", "building_unit_id", "code") : "");
  let buildingUnitName =
    getStr(o, "buildingUnitName", "building_unit_name", "constructionUnitName", "unitConstructionName") ||
    (buNested ? getStr(buNested, "name", "description", "buildingUnitName", "title") : "");

  if (payCat0) {
    buildingUnitId =
      buildingUnitId ||
      getStr(
        payCat0,
        "buildingUnitId",
        "building_unit_id",
        "constructionUnitId",
        "unitConstructionId"
      );
    buildingUnitName =
      buildingUnitName ||
      getStr(
        payCat0,
        "buildingUnitName",
        "building_unit_name",
        "constructionUnitName"
      );
  }

  for (const cat of payCatArr) {
    if (!cat || typeof cat !== "object") continue;
    const c = cat as Record<string, unknown>;
    const cid = getStr(c, "buildingUnitId", "building_unit_id", "constructionUnitId");
    const cname = getStr(c, "buildingUnitName", "building_unit_name", "constructionUnitName");
    if (cid) {
      buildingUnitId = buildingUnitId || cid;
    }
    if (cname) {
      buildingUnitName = buildingUnitName || cname;
    }
    if (buildingUnitId && buildingUnitName) break;
  }

  const resourceId = getStr(
    o,
    "productId",
    "product_id",
    "resourceId",
    "resource_id",
    "insumoId",
    "insumo_id",
    "materialId",
    "material_id"
  );
  const resourceDescription = getStr(
    o,
    "productDescription",
    "product_description",
    "resourceDescription",
    "resource_description",
    "materialDescription",
    "material_description",
    "insumoDescription",
    "insumo_description",
    "serviceDetail",
    "service_detail",
    "itemDescription",
    "item_description",
    "productName",
    "product_name"
  );

  return {
    billId:         getStr(o, "billId", "bill_id", "id"),
    issueDate:      getStr(o, "issueDate", "issue_date", "emissionDate", "documentDate", "createdAt").substring(0, 10),
    dueDate:        getStr(o, "dueDate", "due_date", "expirationDate", "maturityDate").substring(0, 10),
    paymentDate:    paymentDateStr,
    originalAmount,
    ...(usedValorApropriado ? { valorApropriado: valorApropriadoSum! } : {}),
    balanceAmount,
    isPaid,
    creditorName:   getStr(o,
      "creditorName", "creditor_name",
      "supplierName", "supplier_name",
      "businessPartnerName", "personName", "companyName", "name"
    ),
    documentType:   getStr(o,
      "documentIdentificationName", "documentIdentificationId",
      "documentType", "document_type", "docType"
    ),
    costCenterId,
    costCenterName,
    accountId,
    accountName,
    accountCode: accountCode || undefined,
    buildingUnitId:   buildingUnitId || undefined,
    buildingUnitName: buildingUnitName || undefined,
    resourceId:       resourceId || undefined,
    resourceDescription: resourceDescription || undefined,
    description:    getStr(o, "description", "observation", "detailDescription"),
    notes:          getStr(o, "notes", "note", "observation", "comments", "comment", "memo"),
    /* Sienge bulk-data usa installmentId para o número da parcela */
    installmentNumber: getNum(o,
      "installmentId", "installment_id",            /* campo real confirmado no Sienge */
      "installmentNumber", "installment_number",
      "parcelNumber", "parcel_number",
      "numberInstallment", "number_installment",
      "parcela", "parcel"
    ) || 1,
    installmentsTotal: (() => {
      const total = getNum(
        o,
        "installmentsNumber",
        "installments_number",
        "totalInstallments",
        "total_installments",
        "numberInstallments",
        "number_installments"
      );
      return total > 0 ? total : undefined;
    })(),
  };
}


/** Linhas `receipts` na parcela / installment (mesmo modelo do Power Query). */
function collectIncomeReceiptRows(o: Record<string, unknown>): Record<string, unknown>[] {
  const out: Record<string, unknown>[] = [];
  const push = (x: unknown) => {
    if (x == null) return;
    if (Array.isArray(x)) {
      for (const r of x) {
        if (r && typeof r === "object") out.push(r as Record<string, unknown>);
      }
    } else if (typeof x === "object") {
      out.push(x as Record<string, unknown>);
    }
  };
  push(o.receipts);
  push(o.receipt);
  const inst = o.installment ?? o.installment_ ?? o.parcel;
  if (inst && typeof inst === "object" && !Array.isArray(inst)) {
    const io = inst as Record<string, unknown>;
    push(io.receipts);
    push(io.receipt);
  }
  const seen = new Set<string>();
  const deduped: Record<string, unknown>[] = [];
  for (const r of out) {
    const fp = [
      getStr(r, "paymentDate", "payment_date", "creditDate", "credit_date").slice(0, 10),
      getStr(r, "sequencialNumber", "sequencial_number", "sequentialNumber", "id"),
      String(getNum(r, "netAmount", "net_amount")),
      String(getNum(r, "grossAmount", "gross_amount")),
    ].join("|");
    if (seen.has(fp)) continue;
    seen.add(fp);
    deduped.push(r);
  }
  return deduped;
}

function getIncomePaymentDateFromReceiptRow(r: Record<string, unknown>): string {
  return getStr(
    r,
    "paymentDate",
    "payment_date",
    "creditDate",
    "credit_date",
    "calculationDate",
    "calculation_date"
  ).substring(0, 10);
}

/** Bulk Sienge `receipts[]`: igual ao Power Query — só `operationTypeName` exatamente «Recebimento». */
function incomeReceiptIsRecebimento(r: Record<string, unknown>): boolean {
  const raw = String(r.operationTypeName ?? r.operation_type_name ?? "").trim();
  return raw === "Recebimento";
}

/**
 * Uma parcela virando uma ou mais linhas (uma por recebimento quando há `receipts`).
 * Valores monetários alinhados ao bulk: `grossAmount` na coluna Valor, `netAmount` no líquido/KPI.
 */
function normalizeIncomeParcelRow(
  o: Record<string, unknown>,
  receiptLine: Record<string, unknown> | null,
): IncomeItem | null {
  const parcelOriginal = getNum(o,
    "originalAmount", "original_amount",
    "correctedOriginalAmount", "grossAmount", "amount", "value"
  );

  let grossFromReceipt = 0;
  let netFromReceipt: number | undefined;
  if (receiptLine) {
    grossFromReceipt = getNum(receiptLine, "grossAmount", "gross_amount");
    const netRaw = receiptLine.netAmount ?? receiptLine.net_amount;
    if (netRaw != null && netRaw !== "") {
      const n = typeof netRaw === "number" ? netRaw : parseFloat(String(netRaw).replace(",", "."));
      if (!Number.isNaN(n)) netFromReceipt = n;
    }
  }

  const originalAmount =
    receiptLine && grossFromReceipt > 0 ? grossFromReceipt : parcelOriginal;

  if (!receiptLine && parcelOriginal <= 0) return null;

  if (receiptLine) {
    const pdOk = getIncomePaymentDateFromReceiptRow(receiptLine).length >= 10;
    const hasMoney =
      grossFromReceipt > 0 ||
      (netFromReceipt != null && Math.abs(netFromReceipt) > 1e-9) ||
      incomeReceiptAdditionFromRow(receiptLine) !== 0 ||
      incomeReceiptInsuranceFromRow(receiptLine) !== 0 ||
      incomeReceiptDueAdmFromRow(receiptLine) !== 0 ||
      incomeReceiptDiscountFromRow(receiptLine) !== 0;
    if (!hasMoney && !pdOk) return null;
  }

  const balanceAmount = getNum(o,
    "balanceAmount", "balance_amount",
    "correctedBalanceAmount", "corrected_balance_amount",
    "currentBalance", "balance"
  ) || parcelOriginal;

  const paymentDateStr = receiptLine
    ? getIncomePaymentDateFromReceiptRow(receiptLine)
    : getIncomePaymentDateFromRaw(o);
  /* Data de recebimento presente ⇒ quitado (saldo no Sienge pode mentir). */
  const isPaid = paymentDateStr !== "";

  /* Mesma base do outcome + demais *Categories (income costuma aninhar no installment). */
  const payCatArr = collectIncomePaymentsCategories(o);
  const payCat0 = payCatArr.length > 0 ? payCatArr[0] : null;

  const buNested = (o.buildingUnit ?? o.building_unit ?? o.constructionUnit) as
    | Record<string, unknown>
    | undefined;

  let buildingUnitId =
    getStr(o, "buildingUnitId", "building_unit_id", "constructionUnitId", "unitConstructionId") ||
    (buNested ? getStr(buNested, "id", "buildingUnitId", "building_unit_id", "code") : "");
  let buildingUnitName =
    getStr(o, "buildingUnitName", "building_unit_name", "constructionUnitName", "unitConstructionName") ||
    (buNested ? getStr(buNested, "name", "description", "buildingUnitName", "title") : "");

  for (const cat of payCatArr) {
    const cid = getStr(cat, "buildingUnitId", "building_unit_id", "constructionUnitId");
    const cname = getStr(cat, "buildingUnitName", "building_unit_name", "constructionUnitName");
    if (cid) buildingUnitId = buildingUnitId || cid;
    if (cname) buildingUnitName = buildingUnitName || cname;
    if (buildingUnitId && buildingUnitName) break;
  }

  /* Centro de custo: raiz (tag do BFF), paymentsCategories ou objeto costCenter. */
  const ccObj = (o.costCenter ?? o.cost_center) as Record<string, unknown> | undefined;
  let costCenterId =
    getStr(o, "costCenterId", "cost_center_id", "costCenterCode", "cost_center_code") ||
    (payCat0 ? getStr(payCat0, "costCenterId", "cost_center_id") : "") ||
    (ccObj ? getStr(ccObj, "id", "costCenterId", "cost_center_id", "code") : "");
  let costCenterName =
    getStr(o, "costCenterName", "cost_center_name", "costCenterDescription") ||
    (payCat0 ? getStr(payCat0, "costCenterName", "cost_center_name") : "") ||
    (ccObj ? getStr(ccObj, "name", "description", "costCenterName") : "");

  if (!costCenterId) {
    for (const cat of payCatArr) {
      costCenterId = getStr(cat, "costCenterId", "cost_center_id");
      costCenterName =
        costCenterName || getStr(cat, "costCenterName", "cost_center_name");
      if (costCenterId) break;
    }
  }
  if (!costCenterId) {
    const inst = (o.installment ?? o.installment_) as Record<string, unknown> | undefined;
    if (inst && typeof inst === "object") {
      costCenterId =
        getStr(inst, "costCenterId", "cost_center_id") ||
        (() => {
          const icc = (inst.costCenter ?? inst.cost_center) as Record<string, unknown> | undefined;
          return icc ? getStr(icc, "id", "costCenterId", "code") : "";
        })();
      costCenterName =
        costCenterName ||
        getStr(inst, "costCenterName", "cost_center_name") ||
        (() => {
          const icc = (inst.costCenter ?? inst.cost_center) as Record<string, unknown> | undefined;
          return icc ? getStr(icc, "name", "description", "costCenterName") : "";
        })();
    }
  }

  /* Plano financeiro: categorias + raiz + installment + linha de receipt (quando existir). */
  let accountId = getStr(o, ...INCOME_ACCOUNT_ID_KEYS);
  let accountName = getStr(o, ...INCOME_ACCOUNT_NAME_KEYS);
  if (!accountId || !accountName) {
    for (const cat of payCatArr) {
      const fields = accountFieldsFromCategoryRow(cat);
      if (!accountId && fields.id) accountId = fields.id;
      if (!accountName && fields.name) accountName = fields.name;
      if (accountId && accountName) break;
    }
  }
  if (!accountId || !accountName) {
    const inst = (o.installment ?? o.installment_ ?? o.parcel) as Record<string, unknown> | undefined;
    if (inst && typeof inst === "object") {
      if (!accountId) accountId = getStr(inst, ...INCOME_ACCOUNT_ID_KEYS);
      if (!accountName) accountName = getStr(inst, ...INCOME_ACCOUNT_NAME_KEYS);
    }
  }
  if (receiptLine && (!accountId || !accountName)) {
    const fromRec = accountFieldsFromCategoryRow(receiptLine);
    if (!accountId && fromRec.id) accountId = fromRec.id;
    if (!accountName && fromRec.name) accountName = fromRec.name;
    if (!accountId || !accountName) {
      const nestedCats = (receiptLine as Record<string, unknown>).paymentsCategories
        ?? (receiptLine as Record<string, unknown>).receiptCategories
        ?? (receiptLine as Record<string, unknown>).categories;
      if (Array.isArray(nestedCats)) {
        for (const cat of nestedCats) {
          if (!cat || typeof cat !== "object") continue;
          const fields = accountFieldsFromCategoryRow(cat as Record<string, unknown>);
          if (!accountId && fields.id) accountId = fields.id;
          if (!accountName && fields.name) accountName = fields.name;
          if (accountId && accountName) break;
        }
      }
    }
  }

  const receiptSums = receiptLine
    ? {
        addition: incomeReceiptAdditionFromRow(receiptLine),
        insurance: incomeReceiptInsuranceFromRow(receiptLine),
        dueAdm: incomeReceiptDueAdmFromRow(receiptLine),
        discount: incomeReceiptDiscountFromRow(receiptLine),
      }
    : sumIncomeReceiptAmountsFromRaw(o);

  const receiptGrossAmount =
    receiptLine && grossFromReceipt > 0 ? grossFromReceipt : undefined;
  const receiptNetAmount =
    receiptLine && netFromReceipt != null && !Number.isNaN(netFromReceipt)
      ? netFromReceipt
      : undefined;

  const clientId = extractIncomeClientId(o);
  const clientDocument = extractIncomeClientDocument(o);
  const installment = extractIncomeInstallment(o);

  return {
    billId:         getStr(o, "billId", "bill_id", "id"),
    issueDate:      getStr(o, "issueDate", "issue_date", "emissionDate", "documentDate", "createdAt").substring(0, 10),
    dueDate:        getStr(o, "dueDate", "due_date", "expirationDate", "maturityDate").substring(0, 10),
    paymentDate:    paymentDateStr,
    originalAmount,
    balanceAmount,
    isPaid,
    clientId:       clientId || undefined,
    clientDocument: clientDocument || undefined,
    installmentId:  installment.id || undefined,
    installmentNumber: installment.number > 0 ? installment.number : undefined,
    clientName:     getStr(o,
      "clientName", "client_name",
      "customerName", "customer_name",
      "businessPartnerName", "personName", "companyName", "name"
    ),
    documentIdentificationName: (() => {
      const n = getStr(o, "documentIdentificationName", "document_identification_name");
      return n || undefined;
    })(),
    documentType:   getStr(o,
      "documentIdentificationName", "documentIdentificationId",
      "documentType", "document_type", "docType"
    ),
    description:    getStr(o, "description", "notes", "observation", "detailDescription"),
    buildingUnitId:   buildingUnitId || undefined,
    buildingUnitName: buildingUnitName || undefined,
    costCenterId:     costCenterId || undefined,
    costCenterName:   costCenterName || undefined,
    accountId:        accountId || undefined,
    accountName:      accountName || undefined,
    receiptAdditionAmount: receiptSums.addition,
    receiptInsuranceAmount: receiptSums.insurance,
    receiptDueAdmAmount: receiptSums.dueAdm,
    receiptDiscountAmount: receiptSums.discount,
    receiptGrossAmount,
    receiptNetAmount,
  };
}

/** Remove `receipts` para ler só o saldo em aberto da parcela (evita marcar “pago” por recebimento parcial). */
function stripIncomeReceiptArrays(o: Record<string, unknown>): Record<string, unknown> {
  const c: Record<string, unknown> = { ...o };
  delete c.receipts;
  delete c.receipt;
  for (const key of ["installment", "installment_", "parcel"] as const) {
    if (!(key in c)) continue;
    const inst = c[key];
    if (inst && typeof inst === "object" && !Array.isArray(inst)) {
      const ic = { ...(inst as Record<string, unknown>) };
      delete ic.receipts;
      delete ic.receipt;
      c[key] = ic;
    }
  }
  return c;
}

/** Linha sintética só com saldo em aberto — «Contas a receber» quando já houve recebimentos parciais. */
function normalizeIncomeOpenBalanceRow(o: Record<string, unknown>): IncomeItem | null {
  const bal =
    getNum(
      o,
      "balanceAmount",
      "balance_amount",
      "correctedBalanceAmount",
      "corrected_balance_amount",
      "currentBalance",
      "balance"
    ) || 0;
  if (bal <= 0.02) return null;
  const stripped = stripIncomeReceiptArrays(o);
  const base = normalizeIncomeParcelRow(stripped, null);
  if (!base) return null;
  return {
    ...base,
    originalAmount: bal,
    balanceAmount: bal,
    paymentDate: "",
    isPaid: false,
    receiptAdditionAmount: 0,
    receiptInsuranceAmount: 0,
    receiptDueAdmAmount: 0,
    receiptDiscountAmount: 0,
    receiptGrossAmount: undefined,
    receiptNetAmount: undefined,
  };
}

/** Expande cada parcela em N linhas quando há vários objetos em `receipts` (como no Power Query). */
function normalizeIncomeRows(raw: unknown): IncomeItem[] {
  if (!raw || typeof raw !== "object") return [];
  const o = raw as Record<string, unknown>;
  const rows = collectIncomeReceiptRows(o);
  if (rows.length === 0) {
    const one = normalizeIncomeParcelRow(o, null);
    return one ? [one] : [];
  }
  const out: IncomeItem[] = [];
  for (const rec of rows) {
    if (!incomeReceiptIsRecebimento(rec)) continue;
    const item = normalizeIncomeParcelRow(o, rec);
    if (item) out.push(item);
  }
  const tail = normalizeIncomeOpenBalanceRow(o);
  if (tail) out.push(tail);
  return out;
}

function normalizeStatementMovement(raw: unknown, idx: number): BankStatementMovement | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const cidRaw = o.companyId ?? o.company_id;
  if (cidRaw != null && String(cidRaw).trim() !== "") {
    const s = String(cidRaw).trim();
    if (s !== "1" && Number(s) !== 1) return null;
  }

  const accountNumber = getStr(
    o,
    "accountNumber",
    "account_number",
    "bankAccountNumber",
    "bank_account_number",
    "account"
  );
  const accountLabel = semogBankAccountLabel(accountNumber);
  if (!accountLabel) return null;

  const dateRaw = getStr(
    o,
    "movementDate",
    "movement_date",
    "postingDate",
    "posting_date",
    "transactionDate",
    "transaction_date",
    "date",
    "dueDate",
    "due_date"
  );
  const movementDate = dateRaw.substring(0, 10);
  if (movementDate.length < 10) return null;

  let credit = getNum(
    o,
    "creditAmount",
    "credit_amount",
    "creditValue",
    "creditedAmount",
    "credit",
    "entrada",
    "valueCredit",
    "value_credit"
  );
  let debit = getNum(
    o,
    "debitAmount",
    "debit_amount",
    "debitValue",
    "debitedAmount",
    "debit",
    "saida",
    "saída",
    "valueDebit",
    "value_debit"
  );

  const amount = getNum(o, "amount", "value", "movementValue", "movement_value", "valor", "historicValue");

  if (credit <= 0 && debit <= 0 && amount !== 0) {
    const nature = getStr(o, "nature", "movementNature", "movement_nature", "type", "entryType", "entry_type").toUpperCase();
    if (nature === "C" || nature === "CREDIT" || nature === "CRÉDITO" || nature === "ENTRADA") {
      credit = Math.abs(amount);
    } else if (nature === "D" || nature === "DEBIT" || nature === "DÉBITO" || nature === "SAIDA" || nature === "SAÍDA") {
      debit = Math.abs(amount);
    } else if (amount > 0) {
      credit = amount;
    } else {
      debit = -amount;
    }
  }

  if (credit <= 0 && debit <= 0) return null;

  const id =
    getStr(o, "id", "movementId", "movement_id", "historyId", "history_id", "statementId", "sequential", "sequencial") ||
    `stmt-${movementDate}-${idx}`;

  const description = getStr(
    o,
    "history",
    "historic",
    "description",
    "observation",
    "memo",
    "documentNumber",
    "document_number",
    "complement",
    "complemento"
  );

  return {
    id,
    movementDate,
    credit: Math.abs(credit),
    debit: Math.abs(debit),
    description: description || "—",
    accountNumber,
    accountLabel,
  };
}

/** Mesmas contas do saldo bancário SEMOG (`biRoutes` /api/bank-balance). */
function semogBankAccountLabel(accountNumberRaw: string): string | null {
  const raw = String(accountNumberRaw ?? "").trim();
  const d = raw.replace(/\D/g, "");
  if (!raw && !d) return null;
  if (/2972\s*[-–]?\s*6/i.test(raw) || d.includes("29726")) return "Banco do Brasil";
  if (/1493\s*[-–]?\s*1/i.test(raw) || d.includes("14931")) return "CEF Codó";
  if (/3220\s*[-–]?\s*8/i.test(raw) || d.includes("32208")) return "CEF Timon";
  if (/2785139\s*[-–]?\s*2/i.test(raw) || d.includes("27851392")) return "Banco Inter";
  if (/0000002488/i.test(raw) || (d.endsWith("2488") && d.length >= 7)) return "Tesouraria";
  if (/000123\s*[-–]?\s*4/i.test(raw) || d.includes("0001234") || (d.endsWith("1234") && d.length >= 7)) {
    return "Caixa em espécie (Nilsinho)";
  }
  if (/40991085\s*[-–]?\s*6/i.test(raw) || d.includes("409910856")) return "C6 Semog Locações";
  return null;
}

/* ─── Chamadas de API ────────────────────────────────────────────────────── */

/** Dedup ao unir bulk outcome `P` + `D` (mesma parcela nas duas consultas). */
function outcomeRawDedupKey(raw: unknown): string {
  if (!raw || typeof raw !== "object") return "";
  const o = raw as Record<string, unknown>;
  const bill = String(o.billId ?? o.bill_id ?? "").trim();
  const inst = String(
    o.installmentId ??
      o.installment_id ??
      o.installmentNumber ??
      o.installment_number ??
      o.parcelNumber ??
      o.parcel_number ??
      ""
  ).trim();
  const due = String(o.dueDate ?? o.due_date ?? "").trim().slice(0, 10);
  return [bill || "?", inst || due || "?"].join("|");
}

function outcomeRawPaymentRichness(raw: unknown): number {
  if (!raw || typeof raw !== "object") return 0;
  const o = raw as Record<string, unknown>;
  let n = 0;
  if (getOutcomePaymentDateFromRaw(o)) n += 100;
  const pays = o.payments;
  if (Array.isArray(pays)) n += pays.length * 10;
  const bm = o.bankMovements;
  if (Array.isArray(bm)) n += bm.length * 5;
  return n;
}

function pickRicherOutcomeRaw(a: unknown, b: unknown): unknown {
  return outcomeRawPaymentRichness(b) > outcomeRawPaymentRichness(a) ? b : a;
}

export type OutcomeSelectionType = "D" | "P";

/**
 * Busca parcelas de contas a pagar (outcome) no período.
 * Une consultas no Sienge bulk (padrão D+P):
 * - selectionType="D": vencimento no intervalo (Contas a Pagar / abertos por vencimento)
 * - selectionType="P": período — inclui movimentação/pagamento no intervalo mesmo com vencimento fora
 *   (ex.: vence 04/05, pago 05/05 aparece ao filtrar só 05/05)
 * Deduplica por título+parcela; em duplicata mantém o payload com mais dados de pagamento.
 *
 * `opts.selectionTypes`: restringe quais tipos buscar (ex.: só `P` no acumulado do Chão de Obra,
 * evitando dezenas de fatias de `D` e o rate limit 429).
 *
 * Nota: `M` (movimento) não é usado — em vários ambientes o bulk outcome retorna 422 com M.
 *
 * Endpoint: GET /api/outcome
 */
export async function fetchOutcome(
  filters: FilterValues,
  _allCcIds?: string[],
  opts?: { selectionTypes?: OutcomeSelectionType[] },
): Promise<OutcomeItem[]> {
  const common: Record<string, string> = {
    startDate: filters.startDate,
    endDate: filters.endDate,
  };
  const ccFilter = Array.isArray(filters.costCenter) ? filters.costCenter : [];
  if (ccFilter.length > 0) common.costCenterIds = ccFilter.join(",");

  const types: OutcomeSelectionType[] =
    opts?.selectionTypes && opts.selectionTypes.length > 0
      ? [...new Set(opts.selectionTypes)]
      : ["D", "P"];

  const results = await Promise.allSettled(
    types.map((selectionType) =>
      api.get("/api/outcome", { params: { ...common, selectionType } }),
    ),
  );

  const byKey = new Map<string, unknown>();
  let anon = 0;
  const put = (row: unknown) => {
    let k = outcomeRawDedupKey(row);
    if (!k) k = `__anon_${anon++}`;
    const prev = byKey.get(k);
    if (prev === undefined) byKey.set(k, row);
    else byKey.set(k, pickRicherOutcomeRaw(prev, row));
  };
  for (const settled of results) {
    if (settled.status !== "fulfilled") continue;
    const list = Array.isArray(settled.value.data?.data) ? settled.value.data.data : [];
    for (const row of list) put(row);
  }

  const items = [...byKey.values()].flatMap(normalizeOutcomeRowToItems);
  /* Após expandir por `paymentsCategories`, cada linha tem o CC da fatia; a API ainda devolve
   * o payload completo (vários CC no mesmo título) se qualquer alocação bateu o filtro — aqui
   * restringe à seleção da faixa (costCenterIds). */
  return filterOutcomeItemsBySelectedCostCenters(items, ccFilter);
}

/** Mantém só parcelas cuja linha (pós-expand) pertence a um dos centros de custo selecionados. */
export function filterOutcomeItemsBySelectedCostCenters(
  items: OutcomeItem[],
  selectedCostCenterIds: string[],
): OutcomeItem[] {
  const sel = (selectedCostCenterIds ?? []).map((s) => String(s).trim()).filter(Boolean);
  if (sel.length === 0) return items;

  const wanted = new Set<string>();
  for (const id of sel) {
    wanted.add(id);
    if (/^\d+$/.test(id)) wanted.add(String(Number(id)));
  }

  return items.filter((it) => {
    const id = String(it.costCenterId ?? "").trim();
    if (!id) return false;
    if (wanted.has(id)) return true;
    if (/^\d+$/.test(id) && wanted.has(String(Number(id)))) return true;
    return false;
  });
}

/**
 * Mapa billId → total de parcelas (installmentsNumber).
 * Backend: GET /bills com debtorId + janela fixa (ex. 2024-01-01 → hoje); uma agregação paginada + cache.
 */
export async function fetchInstallmentsMap(): Promise<Map<string, number>> {
  try {
    const { data } = await api.get("/api/bills-installments-map");
    const raw: Record<string, number> = data?.map ?? {};
    const map = new Map<string, number>();
    for (const [k, v] of Object.entries(raw)) {
      const n = Number(v);
      if (Number.isFinite(n) && n > 0) map.set(String(k).trim(), n);
    }
    if (map.size > 0) mergeInstallmentTotalsIntoCache(map);
    return map;
  } catch {
    return new Map();
  }
}

/**
 * Totais de parcelas por billId (GET Sienge /bills/{id} ou /installments).
 * Complementa o mapa agregado quando o título não entrou na janela / lista.
 */
export async function fetchBillInstallmentTotalsByIds(billIds: string[]): Promise<Map<string, number>> {
  const unique = [...new Set(billIds.map((s) => String(s).trim()).filter(Boolean))];
  if (unique.length === 0) return new Map();
  const result = readInstallmentTotalsCacheForBillIds(unique);
  const missing = unique.filter((id) => !result.has(id));
  if (missing.length === 0) return result;
  try {
    const { data } = await api.post<{ success?: boolean; map?: Record<string, number> }>(
      "/api/bills-installment-totals",
      { billIds: missing }
    );
    const raw = data?.map ?? {};
    const fromApi = new Map<string, number>();
    for (const [k, v] of Object.entries(raw)) {
      const n = Number(v);
      if (Number.isFinite(n) && n > 0) fromApi.set(String(k).trim(), Math.floor(n));
    }
    if (fromApi.size > 0) mergeInstallmentTotalsIntoCache(fromApi);
    for (const [k, v] of fromApi) result.set(k, v);
    return result;
  } catch {
    return result;
  }
}

/**
 * Busca notas (observações) de uma lista de billIds via GET /api/bills-notes.
 * A rota backend aceita no máximo 50 IDs por chamada; esta função faz batching
 * automático em paralelo.
 * Retorna Map<billId, notes>.
 */
export async function fetchBillsNotes(billIds: string[]): Promise<Map<string, string>> {
  const unique = [...new Set(billIds.filter(Boolean))];
  if (unique.length === 0) return new Map();

  const BATCH = 50;
  const batches: string[][] = [];
  for (let i = 0; i < unique.length; i += BATCH) {
    batches.push(unique.slice(i, i + BATCH));
  }

  const results = await Promise.all(
    batches.map((batch) =>
      api
        .get<{ success: boolean; data: Record<string, string> }>("/api/bills-notes", {
          params: { billIds: batch.join(",") },
        })
        .then((r) => r.data.data)
        .catch(() => ({} as Record<string, string>))
    )
  );

  const map = new Map<string, string>();
  for (const obj of results) {
    for (const [k, v] of Object.entries(obj)) {
      if (v) map.set(k, v);
    }
  }
  return map;
}

export interface PaymentCategory {
  id:   string;
  name: string;
  code: string;
  type: string;
}

/**
 * IDs → nome e código dos planos financeiros (payment categories) Sienge.
 */
export async function fetchPaymentCategoryMaps(): Promise<{
  names: Map<string, string>;
  codes: Map<string, string>;
}> {
  try {
    const { data } = await api.get("/api/payment-categories");
    const list: unknown[] = Array.isArray(data.data) ? data.data : [];
    const names = new Map<string, string>();
    const codes = new Map<string, string>();
    for (const raw of list) {
      if (!raw || typeof raw !== "object") continue;
      const o = raw as Record<string, unknown>;
      const id = o.id != null ? String(o.id).trim() : "";
      const name = o.name != null ? String(o.name).trim() : "";
      const explicitCode =
        (o.code != null ? String(o.code).trim() : "") ||
        (o.codigo != null ? String(o.codigo).trim() : "") ||
        (o.categoryCode != null ? String(o.categoryCode).trim() : "") ||
        (o.financialCategoryCode != null ? String(o.financialCategoryCode).trim() : "") ||
        (o.accountingCode != null ? String(o.accountingCode).trim() : "") ||
        (o.accountPlanCode != null ? String(o.accountPlanCode).trim() : "") ||
        (o.planAccountCode != null ? String(o.planAccountCode).trim() : "") ||
        (o.chartOfAccountsCode != null ? String(o.chartOfAccountsCode).trim() : "") ||
        (o.hierarchyCode != null ? String(o.hierarchyCode).trim() : "");
      /* No Sienge bulk, o "código" exibido no ERP costuma ser o próprio `id` da categoria (ex.: 2100401). */
      const code = explicitCode || id;
      if (id && name) {
        names.set(id, name);
        if (/^\d+$/.test(id)) names.set(String(Number(id)), name);
      }
      if (id && code) {
        codes.set(id, code);
        if (/^\d+$/.test(id)) codes.set(String(Number(id)), code);
      }
    }
    return { names, codes };
  } catch {
    return { names: new Map(), codes: new Map() };
  }
}

/**
 * Busca lista de Planos Financeiros (payment categories) do Sienge.
 * Retorna Map<categoryId, categoryName> pronto para uso.
 * Endpoint: GET /api/payment-categories
 */
export async function fetchPaymentCategories(): Promise<Map<string, string>> {
  const { names } = await fetchPaymentCategoryMaps();
  return names;
}

/** Dedup ao unir bulk income `M` + `D` (mesma parcela pode aparecer nas duas consultas). */
function incomeRawDedupKey(raw: unknown): string {
  if (!raw || typeof raw !== "object") return "";
  const o = raw as Record<string, unknown>;
  const bill = String(o.billId ?? o.bill_id ?? "").trim();
  const inst = String(
    o.installmentId ??
      o.installment_id ??
      o.installmentNumber ??
      o.installment_number ??
      o.parcelNumber ??
      o.parcel_number ??
      ""
  ).trim();
  const due = String(o.dueDate ?? o.due_date ?? "").trim().slice(0, 10);
  return [bill || "?", inst || due || "?"].join("|");
}

/** Igual ao filtro do KPI de Recebimentos (data de recebimento com comprimento yyyy-mm-dd). */
function incomePaymentDateInRange(paymentDate: string, startDate: string, endDate: string): boolean {
  const p = (paymentDate || "").substring(0, 10);
  if (p.length < 10) return false;
  return p >= startDate && p <= endDate;
}

/** Títulos recebidos no período (modal Recebimentos, alinhado ao card e KPI). */
export function filterIncomeRecebidosNoPeriodo(
  income: IncomeItem[],
  startDate: string,
  endDate: string
): IncomeItem[] {
  return income.filter(
    (i) => i.isPaid && incomePaymentDateInRange(i.paymentDate, startDate, endDate),
  );
}

/**
 * Busca recebimentos (income) no período.
 * Une duas consultas ao Sienge bulk: `M` (movimento/recebimento no período, alinhado ao extrato)
 * e `D` (vencimento no período), deduplica, e filtra no cliente por data de recebimento ou vencimento.
 * Endpoint: GET /api/income
 */
export async function fetchIncome(filters: FilterValues, _allCcIds?: string[]): Promise<IncomeItem[]> {
  const ccFilter = Array.isArray(filters.costCenter) ? filters.costCenter : [];

  const common: Record<string, string> = {
    startDate: filters.startDate,
    endDate: filters.endDate,
  };
  if (ccFilter.length > 0) {
    common.costCenterIds = ccFilter.join(",");
  }

  const [{ data: dm }, { data: dd }] = await Promise.all([
    api.get("/api/income", { params: { ...common, selectionType: "M" } }),
    api.get("/api/income", { params: { ...common, selectionType: "D" } }),
  ]);

  const listM: unknown[] = Array.isArray(dm?.data) ? dm.data : [];
  const listD: unknown[] = Array.isArray(dd?.data) ? dd.data : [];

  const byKey = new Map<string, unknown>();
  let anon = 0;
  for (const row of listM) {
    let k = incomeRawDedupKey(row);
    if (!k) k = `__anon_m_${anon++}`;
    if (!byKey.has(k)) byKey.set(k, row);
  }
  for (const row of listD) {
    let k = incomeRawDedupKey(row);
    if (!k) k = `__anon_d_${anon++}`;
    if (!byKey.has(k)) byKey.set(k, row);
  }

  const items = [...byKey.values()].flatMap((raw) => normalizeIncomeRows(raw));

  const { startDate, endDate } = filters;
  const inPeriod = items.filter((item) => {
    if (item.isPaid && item.paymentDate) {
      const p = item.paymentDate.substring(0, 10);
      if (p.length >= 10) return p >= startDate && p <= endDate;
    }
    const d = item.dueDate || item.issueDate;
    if (!d) return true;
    return d >= startDate && d <= endDate;
  });

  /* Espelha despesas: após normalizar, restringe às obras selecionadas no FilterBar. */
  return filterIncomeItemsBySelectedCostCenters(inPeriod, ccFilter);
}

/** Mantém só recebimentos cujo `costCenterId` pertence à seleção da faixa. */
export function filterIncomeItemsBySelectedCostCenters(
  items: IncomeItem[],
  selectedCostCenterIds: string[],
): IncomeItem[] {
  const sel = (selectedCostCenterIds ?? []).map((s) => String(s).trim()).filter(Boolean);
  if (sel.length === 0) return items;

  const wanted = new Set<string>();
  for (const id of sel) {
    wanted.add(id);
    if (/^\d+$/.test(id)) wanted.add(String(Number(id)));
  }

  return items.filter((it) => {
    const id = String(it.costCenterId ?? "").trim();
    if (!id) return false;
    if (wanted.has(id)) return true;
    if (/^\d+$/.test(id) && wanted.has(String(Number(id)))) return true;
    return false;
  });
}

/**
 * Total do card «Recebimentos» — espelho do Power Query: bulk bank-movement (M) + histórico «Recebime».
 * GET /api/bank-movement-recebimentos
 * @returns `undefined` se a API falhar (o card usa legado: income + extrato).
 */
export async function fetchBankMovementRecebimentosTotal(
  startDate: string,
  endDate: string,
  signal?: AbortSignal
): Promise<number | undefined> {
  try {
    const { data } = await api.get("/api/bank-movement-recebimentos", {
      params: { startDate, endDate, companyId: 1 },
      signal,
    });
    if (data?.success === true && typeof data.total === "number") return data.total;
  } catch (e: unknown) {
    const code = typeof e === "object" && e !== null && "code" in e ? String((e as { code?: string }).code) : "";
    if (code === "ERR_CANCELED") return undefined;
  }
  return undefined;
}

function normalizeBankMovementFinanceiroLine(raw: unknown): BankMovementFinanceiroLine | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const id = String(o.id ?? "").trim();
  const movementDate =
    typeof o.movementDate === "string" ? o.movementDate.trim().substring(0, 10) : "";
  const amt = typeof o.amount === "number" ? o.amount : parseFloat(String(o.amount ?? ""));
  const operationName = String(o.operationName ?? "").trim() || "—";
  const historico = String(o.historico ?? "").trim() || "—";
  if (!id || Number.isNaN(amt) || amt <= 0) return null;
  return { id, movementDate, amount: amt, operationName, historico };
}

/**
 * Resumo financeiro por operação no bulk bank-movement (regra Power Query):
 * Pagamento e Recebimento filtrados por originId=BC e operação != Transferência.
 * Inclui listas para exibição nos modais Pagamentos / Recebimentos.
 */
export async function fetchBankMovementFinanceiroSummary(
  startDate: string,
  endDate: string,
  signal?: AbortSignal
): Promise<{
  pagamentosTotal: number;
  recebimentosTotal: number;
  pagamentosItems: BankMovementFinanceiroLine[];
  recebimentosItems: BankMovementFinanceiroLine[];
} | undefined> {
  try {
    const { data } = await api.get("/api/bank-movement-financeiro", {
      params: { startDate, endDate, companyId: 1 },
      signal,
    });
    if (data?.success === true) {
      const pagamentosTotal =
        typeof data.pagamentosTotal === "number" ? data.pagamentosTotal : 0;
      const recebimentosTotal =
        typeof data.recebimentosTotal === "number" ? data.recebimentosTotal : 0;
      const pArr: unknown[] = Array.isArray(data.pagamentosItems) ? data.pagamentosItems : [];
      const rArr: unknown[] = Array.isArray(data.recebimentosItems) ? data.recebimentosItems : [];
      return {
        pagamentosTotal,
        recebimentosTotal,
        pagamentosItems: pArr.map(normalizeBankMovementFinanceiroLine).filter((x): x is BankMovementFinanceiroLine => x !== null),
        recebimentosItems: rArr.map(normalizeBankMovementFinanceiroLine).filter((x): x is BankMovementFinanceiroLine => x !== null),
      };
    }
  } catch (e: unknown) {
    const code = typeof e === "object" && e !== null && "code" in e ? String((e as { code?: string }).code) : "";
    if (code === "ERR_CANCELED") return undefined;
  }
  return undefined;
}

/**
 * Extrato bancário (Sienge /accounts-statements via /api/accounts-statements).
 * Filtra pelas contas SEMOG usadas no card de saldo bancário.
 * Falhas ou cancelamento retornam lista vazia (não bloqueiam o Financeiro).
 */
export async function fetchAccountsStatements(
  startDate: string,
  endDate: string,
  signal?: AbortSignal
): Promise<BankStatementMovement[]> {
  try {
    const { data } = await api.get("/api/accounts-statements", {
      params: { startDate, endDate, companyId: 1 },
      signal,
    });
    const list: unknown[] = Array.isArray(data?.data)
      ? data.data
      : Array.isArray(data?.results)
        ? data.results
        : [];
    const rows = list
      .map((raw, idx) => normalizeStatementMovement(raw, idx))
      .filter((x): x is BankStatementMovement => x !== null);
    return rows.filter((s) => s.movementDate >= startDate && s.movementDate <= endDate);
  } catch (e: unknown) {
    const code = typeof e === "object" && e !== null && "code" in e ? String((e as { code?: string }).code) : "";
    if (code === "ERR_CANCELED") return [];
    return [];
  }
}
